/**
 * Calls the CoachTools API with the signed-in customer's access token.
 *
 * Every API call goes through here, so token attachment and error shape are
 * decided once. A caller that builds its own fetch will eventually forget the
 * Authorization header, and the resulting 401 looks like a sign-in problem
 * rather than a missing header.
 *
 * Paths are relative. In development Vite proxies /api to the Functions host; in
 * production the Static Web App routes it to the linked backend. Same origin in
 * both, so there is no base URL to configure and no CORS to arrange.
 */

/**
 * WHAT A SCREEN PRINTS. `message` is read by a person, so it never names a
 * route or a code: until 25 September it was `${path} failed: ${code}`, and
 * screens that render an error message put "/api/slots failed: http_422" in
 * front of a parent. The path and the code are still here as fields, for the
 * console and for callers that branch on `code`; `detail` carries the old
 * string for logging.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** The API's machine-readable code, e.g. email_already_registered. */
    readonly code: string,
    message: string,
    /**
     * Which input the code is about, when the API says so — a 422 from
     * POST /api/profile answers { error, field }.
     *
     * Carried here rather than re-derived by each caller because several codes
     * are deliberately reused across fields: `required` and `too_long` say
     * nothing about WHICH name was wrong, so guessing from the code alone would
     * put the message under the wrong input.
     */
    readonly field?: string,
    /** `GET /api/slots failed: http_422` — for the console, never for a screen. */
    readonly detail?: string,
    /** The address a family invitation's link is for, on other_email (stage C). */
    readonly email?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * What to put in front of a person when nothing more specific is known.
 *
 * Screens that recognise a code say something better themselves — the cart
 * knows what `slots_unavailable` means. This is the floor, and it says what
 * they can do rather than what went wrong inside.
 */
export function humanMessage(status: number): string {
  if (status === 403) return 'You do not have access to that.';
  if (status === 404) return 'That is not there any more.';
  if (status >= 500) return 'The service is having trouble. Try again in a moment.';
  return 'That did not work. Try again.';
}

/** Fired when the API answers profile_incomplete; use-me.ts asks /api/me again. */
export const PROFILE_GONE = 'coachtools:profile-gone';

/** Thrown when nobody is signed in, so no call was attempted. */
export class NotSignedIn extends Error {
  constructor() {
    super('Not signed in.');
    this.name = 'NotSignedIn';
  }
}

export async function apiFetch<T>(
  path: string,
  getToken: () => Promise<string | null>,
  init: RequestInit = {},
): Promise<T> {
  const token = await getToken();
  if (!token) throw new NotSignedIn();

  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(path, { ...init, headers });

  if (!response.ok) {
    // The API answers errors as { error: "code" }. Anything else means the
    // request never reached it - a proxy failure, the Functions host not
    // running - and the status is the only information available.
    let code = `http_${response.status}`;
    let field: string | undefined;
    let email: string | undefined;
    try {
      const body = (await response.json()) as { error?: string; field?: string; email?: string };
      if (body?.error) code = body.error;
      if (typeof body?.field === 'string') field = body.field;
      if (typeof body?.email === 'string') email = body.email;
    } catch {
      // Not JSON. Keep the status-derived code.
    }
    // The family has gone from under this page - most often a second parent the
    // first just removed (ruled 17 October). /api/me is asked again, so the
    // next screen is the removed-access one, not "That did not work" on every
    // tab until a reload nobody knows to do.
    if (code === 'profile_incomplete') window.dispatchEvent(new Event(PROFILE_GONE));
    throw new ApiError(
      response.status,
      code,
      humanMessage(response.status),
      field,
      `${path} failed: ${code}`,
      email,
    );
  }

  // A 204 has no body, and response.json() on an empty one throws a syntax
  // error that reads like a broken API. DELETE /api/availability/{blockId}
  // answers this way, and it is the right answer: the block is gone, and there
  // is nothing to say about it. Callers of a 204 route declare Promise<void>.
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}
